import React, { useState } from 'react';
import { Modal } from 'antd';
import { submitSupportRequest } from '../../../api';
import messageQueue from '../../../utils/messageQueue';
import MessageConstant from '../../../utils/Constant/MessageConstant';
import styles from './RefundFormModal.module.scss';

const formatAmountInput = (rawValue) => {
    const sanitizedValue = rawValue.replace(/[^\d.]/g, '');
    const [integerPart = '', decimalPart = ''] = sanitizedValue.split('.');
    const normalizedInteger = integerPart.replace(/^0+(?=\d)/, '');
    const formattedInteger = normalizedInteger
        ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(normalizedInteger))
        : '';

    if (sanitizedValue.includes('.')) {
        return `${formattedInteger || '0'}.${decimalPart.slice(0, 2)}`;
    }

    return formattedInteger;
};

const normalizeAmountForSubmission = (formattedValue) => formattedValue.replace(/,/g, '').trim();

const RefundFormModal = ({ visible, onClose }) => {
    const [reason, setReason] = useState('');
    const [orderDate, setOrderDate] = useState('');
    const [amount, setAmount] = useState('');
    const [explanation, setExplanation] = useState('');
    const [preferredMethod, setPreferredMethod] = useState('original');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const reasons = [
        { value: 'unsatisfied', label: 'Service not as expected', icon: '😔' },
        { value: 'technical', label: 'Technical issues', icon: '⚠️' },
        { value: 'duplicate', label: 'Duplicate charge', icon: '📋' },
        { value: 'unused', label: 'Did not use service', icon: '❌' },
        { value: 'other', label: 'Other reason', icon: '💬' }
    ];

    const refundMethods = [
        { value: 'original', label: 'Refund to original payment method' },
        { value: 'credit', label: 'Account credit for future use' }
    ];

    const handleCancel = () => {
        setReason('');
        setOrderDate('');
        setAmount('');
        setExplanation('');
        setPreferredMethod('original');
        onClose();
    };

    const handleSubmit = async () => {
        if (!reason || !orderDate || !amount || !explanation) {
            messageQueue.warning('VALIDATION_REQUIRED_FIELDS', MessageConstant.VALIDATION_REQUIRED_FIELDS);
            return;
        }

        setIsSubmitting(true);
        try {
            await submitSupportRequest('refund', {
                reason,
                orderDate,
                amount: normalizeAmountForSubmission(amount),
                explanation,
                preferredMethod,
                currentPage: window.location.pathname,
                currentUrl: window.location.href,
                userAgent: navigator.userAgent,
            });
            messageQueue.success('REFUND_REQUEST_SUCCESS', MessageConstant.REFUND_REQUEST_SUCCESS);
            handleCancel();
        } catch (error) {
            messageQueue.error('REFUND_REQUEST_ERROR', error.response?.data?.error || 'Failed to submit refund request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={handleCancel}
            footer={null}
            centered
            width={700}
            className={styles.refundModal}
        >
            <div className={styles.modalContent}>
                {/* Header */}
                <div className={styles.header}>
                    <svg className={styles.refundIcon} width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="24" cy="24" r="20" fill="#FFF7ED" />
                        <path d="M24 14V34M24 14L18 20M24 14L30 20" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14 28H34" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <h2 className={styles.title}>Request Refund</h2>
                    <p className={styles.subtitle}>Please provide details about your refund request</p>
                </div>

                {/* Reason Selection */}
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Reason for Refund <span className={styles.required}>*</span></label>
                    <div className={styles.reasonGrid}>
                        {reasons.map((r) => (
                            <div
                                key={r.value}
                                className={`${styles.reasonCard} ${reason === r.value ? styles.selected : ''}`}
                                onClick={() => setReason(r.value)}
                            >
                                <span className={styles.reasonIcon}>{r.icon}</span>
                                <span className={styles.reasonLabel}>{r.label}</span>
                                <div className={styles.checkmark}>✓</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Order Information */}
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Order Information</label>
                    <div className={styles.orderInfo}>
                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>Order Date <span className={styles.required}>*</span></label>
                            <input
                                type="date"
                                className={styles.textInput}
                                value={orderDate}
                                onChange={(e) => setOrderDate(e.target.value)}
                            />
                        </div>
                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>Amount <span className={styles.required}>*</span></label>
                            <input
                                type="text"
                                className={styles.textInput}
                                placeholder="$0.00"
                                value={amount}
                                inputMode="decimal"
                                onChange={(e) => setAmount(formatAmountInput(e.target.value))}
                            />
                        </div>
                    </div>
                </div>

                {/* Explanation */}
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Detailed Explanation <span className={styles.required}>*</span></label>
                    <textarea
                        className={styles.textArea}
                        placeholder="Please provide a detailed explanation for your refund request..."
                        value={explanation}
                        onChange={(e) => setExplanation(e.target.value)}
                        rows={5}
                    />
                </div>

                {/* Refund Method */}
                <div className={styles.section}>
                    <label className={styles.sectionLabel}>Preferred Refund Method</label>
                    <div className={styles.methodList}>
                        {refundMethods.map((method) => (
                            <div
                                key={method.value}
                                className={`${styles.methodItem} ${preferredMethod === method.value ? styles.selected : ''}`}
                                onClick={() => setPreferredMethod(method.value)}
                            >
                                <div className={styles.radioButton}>
                                    {preferredMethod === method.value && <div className={styles.radioInner} />}
                                </div>
                                <span className={styles.methodText}>{method.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Warning Box */}
                <div className={styles.warningBox}>
                    <svg className={styles.warningIcon} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#f59e0b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className={styles.warningText}>
                        <strong>Important Notice:</strong>
                        <span>Refund requests are reviewed within 3-5 business days. Approved refunds may take 7-10 business days to process depending on your payment method.</span>
                    </div>
                </div>

                {/* Actions */}
                <div className={styles.actions}>
                    <button className={styles.cancelButton} onClick={handleCancel}>
                        Cancel
                    </button>
                    <button
                        className={styles.submitButton}
                        onClick={handleSubmit}
                        disabled={!reason || !orderDate || !amount || !explanation || isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Refund Request'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default RefundFormModal;
